/**
 * Client for the code-mode agent stream (POST /studio/chat/stream).
 *
 * Uses fetch + ReadableStream (not EventSource) so we can send the Bearer auth header.
 * The server-side agent edits the workspace files on disk and emits a structured event
 * per action: 'log' (progress line), 'file' (path written), 'delete', 'rename',
 * 'message' (final summary), 'done', 'error'. The frontend mirrors file/delete/rename
 * into the WebContainer for live HMR (the server has already persisted them).
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface ProjectStreamCallbacks {
    onLog?: (line: string) => void;
    /** Model narrative (codex reasoning) — rendered as prose in the chat bubble. */
    onReasoning?: (text: string) => void;
    onFile?: (path: string, contents: string) => void;
    onDelete?: (path: string) => void;
    onRename?: (from: string, to: string) => void;
    onMessage?: (message: string) => void;
    onDone?: () => void;
    onError?: (message: string) => void;
    /** Turn rejected before starting: the user already has a build running. */
    onBusy?: (message: string) => void;
}

/** Trigger a server-side build (npm install + vite build) and stream its log via SSE. */
export async function buildProject(
    applicationId: string,
    callbacks: { onLog?: (line: string) => void; onDone?: (url: string) => void; onError?: (message: string) => void },
    signal?: AbortSignal,
): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/studio/applications/${applicationId}/build`, {
        method: 'POST',
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        signal,
    });
    if (!res.ok || !res.body) {
        callbacks.onError?.(`Build request failed (${res.status})`);
        return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let event: any;
            try {
                event = JSON.parse(line.slice(5).trim());
            } catch {
                continue;
            }
            if (event.type === 'log') callbacks.onLog?.(event.value);
            else if (event.type === 'done') callbacks.onDone?.(new URL(event.servePath, API_BASE_URL).href);
            else if (event.type === 'error') callbacks.onError?.(event.message || 'Build error');
        }
    }
}

/**
 * Publish the app (server builds the workspace, pushes the `published` branch,
 * activates {subdomain}.hyvay.com). Streams build logs; `onDone` gets the live URL.
 */
export async function publishProject(
    body: { applicationId: string; subdomain: string; customDomain?: string },
    callbacks: { onLog?: (line: string) => void; onDone?: (data: { url: string }) => void; onError?: (message: string) => void },
    signal?: AbortSignal,
): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/studio/publish/stream`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
    });
    if (!res.ok || !res.body) {
        callbacks.onError?.(`Publish request failed (${res.status})`);
        return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let event: any;
            try {
                event = JSON.parse(line.slice(5).trim());
            } catch {
                continue;
            }
            if (event.type === 'log') callbacks.onLog?.(event.value);
            else if (event.type === 'done') callbacks.onDone?.(event.data);
            else if (event.type === 'error') callbacks.onError?.(event.message || 'Publish error');
        }
    }
}

/** Is a background generation currently running for this app? */
export async function getTurnStatus(
    applicationId: string,
): Promise<{ running: boolean; prompt?: string; startedAt?: number }> {
    const token = localStorage.getItem('auth_token');
    try {
        const res = await fetch(`${API_BASE_URL}/studio/applications/${applicationId}/turn`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return { running: false };
        const body = await res.json();
        return body?.data ?? { running: false };
    } catch {
        return { running: false };
    }
}

/**
 * Reattach to a running background generation: the server replays everything
 * that already happened, then follows live. Same event contract as streamProject.
 */
export async function attachTurn(
    applicationId: string,
    callbacks: ProjectStreamCallbacks,
    signal?: AbortSignal,
): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/studio/applications/${applicationId}/turn/stream`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal,
    });
    if (!res.ok || !res.body) {
        callbacks.onError?.(`Reattach failed (${res.status})`);
        return;
    }
    await consumeProjectEvents(res.body, callbacks);
}

export async function streamProject(
    body: { applicationId: string; pageId?: string; prompt: string },
    callbacks: ProjectStreamCallbacks,
    signal?: AbortSignal,
): Promise<void> {
    const token = localStorage.getItem('auth_token');
    const res = await fetch(`${API_BASE_URL}/studio/chat/stream`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
        signal,
    });

    if (!res.ok || !res.body) {
        callbacks.onError?.(`Stream request failed (${res.status})`);
        return;
    }
    await consumeProjectEvents(res.body, callbacks);
}

/** Shared SSE parser for agent-turn streams (live and reattached). */
async function consumeProjectEvents(body: ReadableStream<Uint8Array>, callbacks: ProjectStreamCallbacks): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line.
        const frames = buffer.split('\n\n');
        buffer = frames.pop() ?? '';
        for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'));
            if (!line) continue;
            let event: any;
            try {
                event = JSON.parse(line.slice(5).trim());
            } catch {
                continue;
            }
            switch (event.type) {
                case 'log':
                    callbacks.onLog?.(event.value);
                    break;
                case 'reasoning':
                    callbacks.onReasoning?.(event.text);
                    break;
                case 'file':
                    callbacks.onFile?.(event.path, event.contents);
                    break;
                case 'delete':
                    callbacks.onDelete?.(event.path);
                    break;
                case 'rename':
                    callbacks.onRename?.(event.from, event.to);
                    break;
                case 'message':
                    callbacks.onMessage?.(event.value);
                    break;
                case 'done':
                    callbacks.onDone?.();
                    break;
                case 'error':
                    callbacks.onError?.(event.message || 'Stream error');
                    break;
                case 'busy':
                    // Older callers without onBusy still get a visible failure.
                    if (callbacks.onBusy) callbacks.onBusy(event.message || 'Masih ada build yang berjalan.');
                    else callbacks.onError?.(event.message || 'Masih ada build yang berjalan.');
                    break;
            }
        }
    }
}
